import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { upsertBlogPost, deleteBlogPost, checkIsAdmin, listAllBlogPostsAdmin } from "@/lib/blog.functions";
import { Plus, Pencil, Trash2 } from "lucide-react";

export const Route = createFileRoute("/admin/blogg")({
  head: () => ({ meta: [{ title: "Admin · Blogg" }, { name: "robots", content: "noindex" }] }),
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/login" });
    const { isAdmin } = await checkIsAdmin();
    if (!isAdmin) throw redirect({ to: "/" });
  },
  component: AdminBlogg,
});

type Post = {
  id: string; slug: string; title: string; excerpt: string | null;
  content: string; cover_url: string | null; published: boolean; published_at: string | null;
};

function AdminBlogg() {
  const qc = useQueryClient();
  const upsert = useServerFn(upsertBlogPost);
  const remove = useServerFn(deleteBlogPost);
  const listAll = useServerFn(listAllBlogPostsAdmin);
  const [editing, setEditing] = useState<Partial<Post> | null>(null);

  const { data: posts } = useQuery({
    queryKey: ["admin_posts"],
    queryFn: async () => {
      const { posts } = await listAll();
      return posts as Post[];
    },
  });

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!editing) return;
    const fd = new FormData(e.currentTarget);
    const payload = {
      id: editing.id,
      slug: String(fd.get("slug") ?? "").trim(),
      title: String(fd.get("title") ?? "").trim(),
      excerpt: String(fd.get("excerpt") ?? "").trim(),
      content: String(fd.get("content") ?? "").trim(),
      cover_url: String(fd.get("cover_url") ?? "").trim(),
      published: fd.get("published") === "on",
    };
    try {
      await upsert({ data: payload });
      toast.success("Sparat");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["admin_posts"] });
      qc.invalidateQueries({ queryKey: ["blog_posts"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Fel vid sparande");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Ta bort inlägget?")) return;
    try {
      await remove({ data: { id } });
      toast.success("Borttaget");
      qc.invalidateQueries({ queryKey: ["admin_posts"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Fel");
    }
  }

  const inputCls = "w-full rounded-2xl border border-input bg-background px-4 py-3 text-base outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20";

  return (
    <section className="container-edge max-w-5xl py-16">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl">
            Hantera <span className="font-italic-display text-accent">blogg</span>.
          </h1>
        </div>
        <button
          onClick={() => setEditing({ published: false })}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
        >
          <Plus size={16} /> Nytt inlägg
        </button>
      </div>

      {editing && (
        <form onSubmit={handleSave} className="mt-10 space-y-4 rounded-3xl border border-border bg-card p-6 md:p-8 soft-shadow">
          <p className="eyebrow">{editing.id ? "Redigera" : "Nytt"}</p>
          <input name="title" defaultValue={editing.title ?? ""} placeholder="Titel" required className={inputCls} />
          <input name="slug" defaultValue={editing.slug ?? ""} placeholder="slug-i-url" required pattern="[a-z0-9-]+" className={`${inputCls} font-mono text-sm`} />
          <input name="cover_url" defaultValue={editing.cover_url ?? ""} placeholder="Omslagsbild URL (valfritt)" className={inputCls} />
          <input name="excerpt" defaultValue={editing.excerpt ?? ""} placeholder="Kort sammanfattning" className={inputCls} />
          <textarea name="content" defaultValue={editing.content ?? ""} placeholder="Innehåll" required rows={10} className={inputCls} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="published" defaultChecked={!!editing.published} className="h-4 w-4 rounded border-border" /> Publicerad
          </label>
          <div className="flex gap-3">
            <button className="rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Spara</button>
            <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-border bg-card px-5 py-3 text-sm font-medium hover:bg-secondary">Avbryt</button>
          </div>
        </form>
      )}

      <div className="mt-10 overflow-hidden rounded-3xl border border-border bg-card divide-y divide-border">
        {posts?.length === 0 && <p className="p-6 text-sm text-muted-foreground">Inga inlägg än.</p>}
        {posts?.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-4 p-5">
            <div className="min-w-0">
              <p className="truncate font-display text-xl">{p.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                /{p.slug} · {p.published ? <span className="text-accent">Publicerad</span> : "Utkast"}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => setEditing(p)} className="rounded-full border border-border bg-background p-2.5 hover:bg-secondary" aria-label="Redigera"><Pencil size={15} /></button>
              <button onClick={() => handleDelete(p.id)} className="rounded-full border border-border bg-destructive p-2.5 text-destructive-foreground hover:opacity-90" aria-label="Ta bort"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {!posts && <p className="p-6 text-sm text-muted-foreground">Laddar…</p>}
      </div>

      <div className="mt-8">
        <Link to="/" className="text-sm text-muted-foreground hover:text-accent">← Till hem</Link>
      </div>
    </section>
  );
}
